import axios from 'axios';

const sleep = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

export const getPlugins = (token) => axios.get(`${process.env.REACT_APP_API_URL}/plugins`, {
  headers: { Authorization: `${token}` },
});

/** Shared daily trivia MP4s for config preview rotation. */
export const getTriviaToday = (token, orientation = 'landscape') => axios.get(
  `${process.env.REACT_APP_API_URL}/plugins/trivia/today`,
  {
    params: { orientation },
    headers: { Authorization: `${token}` },
  },
);

export const searchWeatherLocations = (query, token) => axios.post(
  `${process.env.REACT_APP_API_URL}/plugins/weather/locations`,
  { query },
  { headers: { Authorization: `${token}` } },
);

export const getSportsLeagues = (token) => axios.post(
  `${process.env.REACT_APP_API_URL}/plugins/sports/leagues`,
  {},
  { headers: { Authorization: `${token}` } },
);

export const getSportsEvents = (sportKey, token, dates) => axios.post(
  `${process.env.REACT_APP_API_URL}/plugins/sports/events`,
  { sportKey, dates },
  { headers: { Authorization: `${token}` } },
);

export const searchSportsTeams = (query, sportKey, token) => axios.post(
  `${process.env.REACT_APP_API_URL}/plugins/sports/teams/search`,
  { query, sportKey },
  { headers: { Authorization: `${token}` } },
);

export const getPluginJob = (jobId, token) => axios.get(
  `${process.env.REACT_APP_API_URL}/plugins/jobs/${jobId}`,
  { headers: { Authorization: `${token}` } },
);

export const getActivePluginJob = (screenId, slotId, token) => axios.get(
  `${process.env.REACT_APP_API_URL}/plugins/jobs/active`,
  {
    params: { screenId, slotId },
    headers: { Authorization: `${token}` },
  },
);

/** One request for all in-progress user render jobs on a screen (initial load / refresh only). */
export const getActivePluginJobs = (screenId, token) => axios.get(
  `${process.env.REACT_APP_API_URL}/plugins/jobs/active`,
  {
    params: { screenId },
    headers: { Authorization: `${token}` },
  },
);

export async function pollPluginJob(jobId, token, options = {}) {
  const intervalMs = options.intervalMs || 2500;
  const timeoutMs = options.timeoutMs || 300000;
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    const res = await getPluginJob(jobId, token);
    const job = res.data?.job;
    const status = job?.status;
    if (status === 'completed') {
      return job;
    }
    if (status === 'failed' || status === 'dead') {
      const err = new Error(job?.lastError || 'Plugin render failed');
      err.job = job;
      throw err;
    }
    await sleep(intervalMs);
  }

  const err = new Error('Plugin render timed out. The job may still complete in the background.');
  err.code = 'PLUGIN_JOB_TIMEOUT';
  throw err;
}

export const enqueuePluginSlot = (payload, token) => axios.post(
  `${process.env.REACT_APP_API_URL}/plugins/configureSlot`,
  payload,
  { headers: { Authorization: `${token}` }, timeout: 30000 },
);

/** @deprecated Use enqueuePluginSlot + pollPluginJob / usePluginSlotJobs instead */
export const configurePluginSlot = async (payload, token) => {
  const res = await enqueuePluginSlot(payload, token);
  const jobId = res.data?.jobId;
  if (jobId) {
    await pollPluginJob(jobId, token);
  }
  return res;
};

export const clearContentSlot = (payload, token) => axios.post(
  `${process.env.REACT_APP_API_URL}/plugins/clearSlot`,
  payload,
  { headers: { Authorization: `${token}` } },
);

/* --------------------------- Social media --------------------------- */

export const getSocialConnections = (userId, token, provider) => axios.post(
  `${process.env.REACT_APP_API_URL}/social/connections/list`,
  { userId, provider },
  { headers: { Authorization: `${token}` } },
);

export const connectSocialAccount = (userId, provider, token, appRedirect) => axios.post(
  `${process.env.REACT_APP_API_URL}/social/connect/${provider}`,
  { userId, appRedirect },
  { headers: { Authorization: `${token}` } },
);

export const deleteSocialConnection = (userId, connectionId, token) => axios.post(
  `${process.env.REACT_APP_API_URL}/social/connections/delete`,
  { userId, connectionId },
  { headers: { Authorization: `${token}` } },
);

export const getSocialPosts = (payload, token) => axios.post(
  `${process.env.REACT_APP_API_URL}/social/posts`,
  payload,
  { headers: { Authorization: `${token}` }, timeout: 30000 },
);
